import { Box, GlobalStyles, styled } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { DEFAULT_DRAWER_WIDTH } from "src/constants";
import { useSettings } from "src/hooks";

const MIN_DRAWER_WIDTH = DEFAULT_DRAWER_WIDTH;
const MAX_DRAWER_WIDTH = 1000;

const Container = styled(Box)<{ $open: boolean; $isDragging: boolean }>`
  background-color: #fff;
  box-shadow: rgba(0, 0, 0, 0.05) -3px 0px 5px;
  flex-shrink: 0;
  flex-basis: ${({ $open }) => ($open ? undefined : "0 !important")};
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  position: sticky;
  top: 0;
  transition: ${({ theme, $isDragging }) => ($isDragging ? undefined : theme.transitions.create("flex-basis"))};
  z-index: 998;
`;

const Dragger = styled(Box)<{ $isDragging: boolean; $isVisible: boolean }>`
  width: 8px;
  cursor: ew-resize;
  position: fixed;
  top: 0;
  bottom: 0;
  z-index: 100;

  > div {
    opacity: ${({ $isDragging, $isVisible }) => ($isDragging || $isVisible ? 1 : 0)};
  }

  &:hover {
    > div {
      opacity: 1;
    }
  }
`;

const DragIndicator = styled(Box)`
  width: 3px;
  height: 100%;
  background-color: ${({ theme }) => theme.palette.focus.main};
  transition: ${({ theme }) => theme.transitions.create("opacity")};
`;

interface IProps {
  open: boolean;
  children: React.ReactNode;
}

const ResizeableDrawer = (props: IProps) => {
  const { drawerSettings, updateDrawerSettings } = useSettings();
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggerVisible, setIsDraggerVisible] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.open) {
      setTimeout(() => {
        setIsDraggerVisible(true);
        setTimeout(() => {
          setIsDraggerVisible(false);
        }, 1000);
      }, 500);
    }
  }, [props.open]);

  useEffect(() => {
    if (drawerRef.current) {
      drawerRef.current.style.flexBasis = `${Math.max(drawerSettings.width, MIN_DRAWER_WIDTH)}px`;
    }
  }, [drawerSettings.width, props.open]);

  function handleMouseDown() {
    setIsDragging(true);
    document.addEventListener("mouseup", handleMouseUp, true);
    document.addEventListener("mousemove", handleMouseMove, true);
  }

  function handleMouseUp() {
    setIsDragging(false);
    document.removeEventListener("mouseup", handleMouseUp, true);
    document.removeEventListener("mousemove", handleMouseMove, true);

    if (drawerRef.current) {
      const drawerWidth = parseInt(drawerRef.current.style.flexBasis.replace("px", ""));
      updateDrawerSettings({
        ...drawerSettings,
        width: drawerWidth,
      });
    }
  }

  function handleMouseMove(event: MouseEvent) {
    event.preventDefault();

    if (drawerRef.current) {
      const mouseDistanceFromRightEdge = window.innerWidth - event.clientX;
      const newWidth = Math.min(Math.max(mouseDistanceFromRightEdge, MIN_DRAWER_WIDTH), MAX_DRAWER_WIDTH);

      drawerRef.current.style.flexBasis = `${newWidth}px`;
    }
  }

  return (
    <Container ref={drawerRef} $open={props.open} $isDragging={isDragging}>
      {isDragging && <GlobalStyles styles={{ body: { userSelect: "none" } }} />}

      <Dragger $isDragging={isDragging} $isVisible={isDraggerVisible} onMouseDown={handleMouseDown}>
        <DragIndicator />
      </Dragger>

      {props.children}
    </Container>
  );
};

export { ResizeableDrawer };
